import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/api/client'
import type { CrewMember, ProvisionalPerson } from '@/api/types'

/**
 * Matching field-created placeholders to real Sage employees.
 *
 * A new hire mobs straight to the field, does the packet with the supervisor
 * on site, and works for days before the office receives the paperwork and
 * enters them in Sage. The supervisor records them as a placeholder so the
 * hours exist at all. This is where those placeholders get resolved.
 *
 * IT IS A QUEUE, NOT A REPORT. Every row is somebody whose hours cannot reach
 * Sage — the key was never issued by Sage and the import would reject it. The
 * hours behind each row are shown because they say which one matters most: a
 * placeholder carrying 40 hours is a person waiting to be paid, and one
 * carrying none is somebody a supervisor added and never used.
 *
 * Linking rewrites every time entry onto the real key, after which the hours
 * are indistinguishable from any other. That is not reversible here, which is
 * why the confirm names the person and the hours rather than asking "are you
 * sure".
 */
export function ProvisionalPeoplePage() {
  const [rows, setRows] = useState<ProvisionalPerson[]>([])
  const [roster, setRoster] = useState<CrewMember[]>([])
  const [showLinked, setShowLinked] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)

  /** Which real employee each row is pointed at, before it is committed. */
  const [choice, setChoice] = useState<Record<number, string>>({})
  const [linking, setLinking] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [p, r] = await Promise.all([
        api.provisionalPeople(showLinked),
        api.crew(),
      ])
      setRows(p)
      setRoster(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the queue')
    } finally {
      setLoading(false)
    }
  }, [showLinked])

  useEffect(() => {
    void load()
  }, [load])

  // Placeholders must never be offered as a link TARGET — linking one
  // placeholder to another would move hours sideways and resolve nothing. The
  // API refuses it too; this keeps it off the screen in the first place.
  const linkTargets = useMemo(
    () =>
      roster
        // Provisional numbering is P plus four digits, against Sage's F####.
        .filter((c) => !/^P\d{4}$/.test(c.employee_no))
        .slice()
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [roster],
  )

  async function link(row: ProvisionalPerson) {
    const target = choice[row.employee_id]
    if (!target) return
    const label =
      linkTargets.find((c) => c.sage_employee_key === target)?.display_name ??
      'that employee'
    if (
      !confirm(
        `Move ${row.display_name}'s ${Number(row.total_hours).toFixed(2)} hours ` +
          `onto ${label}?\n\nThis rewrites ${row.line_count} time ` +
          `${row.line_count === 1 ? 'entry' : 'entries'} and cannot be undone here.`,
      )
    ) {
      return
    }
    setLinking(row.employee_id)
    setError(null)
    try {
      const next = await api.linkProvisional(row.employee_id, target)
      setRows(next)
      setFlash(`${row.display_name} matched up. ${row.line_count} entries moved.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not link that person')
    } finally {
      setLinking(null)
    }
  }

  if (loading && rows.length === 0) {
    return <div className="card">Loading…</div>
  }

  return (
    <div>
      <h1>New people from the field</h1>
      <p className="muted">
        Supervisors can record someone who is working before the office has
        entered them in Sage. Their hours sit here until you say who they are.
        <strong> Nothing on this list can be exported to Sage.</strong>
      </p>

      {flash && <div className="alert alert-ok">{flash}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <label className="checkline" style={{ marginBottom: '0.75rem' }}>
        <input
          type="checkbox"
          checked={showLinked}
          onChange={(e) => setShowLinked(e.target.checked)}
        />
        Include ones already matched up
      </label>

      {rows.length === 0 ? (
        <div className="card">
          <p className="muted">
            {showLinked
              ? 'Nobody has been added from the field yet.'
              : 'Nothing waiting. Every person the field added has been matched up.'}
          </p>
        </div>
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name as entered</th>
                  <th>What the supervisor said</th>
                  <th>Added by</th>
                  <th className="num">Lines</th>
                  <th className="num">Hours</th>
                  <th>Who they actually are</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employee_id}>
                    <td>
                      <strong>{r.display_name}</strong>
                      <div className="mono muted" style={{ fontSize: '0.75rem' }}>
                        {r.employee_no}
                      </div>
                    </td>
                    <td>{r.note ?? <span className="muted">—</span>}</td>
                    <td>{r.added_by ?? <span className="muted">—</span>}</td>
                    <td className="num">{r.line_count}</td>
                    <td className="num">
                      {/* The number that decides what to work on first. */}
                      {Number(r.total_hours).toFixed(2)}
                    </td>
                    <td>
                      {r.linked_employee_id ? (
                        <span className="badge badge-ok">
                          {r.linked_name ?? 'matched'}
                        </span>
                      ) : (
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <select
                            value={choice[r.employee_id] ?? ''}
                            onChange={(e) =>
                              setChoice((c) => ({
                                ...c,
                                [r.employee_id]: e.target.value,
                              }))
                            }
                            aria-label={`Who ${r.display_name} actually is`}
                          >
                            <option value="">Pick the real employee…</option>
                            {linkTargets.map((c) => (
                              <option key={c.sage_employee_key} value={c.sage_employee_key}>
                                {c.display_name} · {c.employee_no}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn"
                            disabled={
                              !choice[r.employee_id] || linking === r.employee_id
                            }
                            onClick={() => void link(r)}
                          >
                            {linking === r.employee_id ? 'Moving…' : 'Match'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
